import { Box, Button, createStyles, Title } from '@mantine/core';
import { find } from 'lodash/fp';
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEffectOnce } from 'react-use';

import {
  SpaceType,
  useCheckQrCodeAccess,
  useClaimDeviceByQrCode,
  useIsCecEnabled,
  useSpaces,
} from '@portals/api/organizations';
import { usePermissionAccess } from '@portals/framework';
import { useOpenRouteModal } from '@portals/framework/route-modals';
import { useOpenModal } from '@portals/redux';

import { QR_SEARCH_PARAM } from '../../../../constants/global-search-params.constants';
import { canView, noAccess } from '../../../../lib/access';
import { SpaceTree } from '../../../components/common/SpaceTree/SpaceTree';
import { PageContainer } from '../../../components/layout/Page';

export function Overview() {
  const { isAdmin } = usePermissionAccess();

  const { classes } = useStyles({ isAdmin });
  const spaces = useSpaces();
  const navigate = useNavigate();
  const openModal = useOpenModal();
  const openRouteModal = useOpenRouteModal();

  const isCecEnabled = useIsCecEnabled();
  const checkQrCodeAccess = useCheckQrCodeAccess();
  const claimDeviceByQrCode = useClaimDeviceByQrCode();

  const params = useParams<{ cloudId: string }>();
  const [searchParams] = useSearchParams();

  const [selectedSpace, setSelectedSpace] = useState<SpaceType | null>(null);

  const onClaim = (cloudId: string) => {
    if (isCecEnabled) {
      checkQrCodeAccess.mutate(
        { qr: cloudId },
        {
          onSuccess: (response) => {
            if (response.claim_allowed) {
              onClaimAllowedDevice(cloudId);
            } else {
              openRouteModal({
                modalId: 'connect',
                pathParams: [response.device_partner_name, cloudId],
                backgroundPath: `/devices/${response.device_id}`,
                replace: true,
              });
            }
          },
        }
      );
    }
  };

  const onClaimAllowedDevice = (qrCode: string) => {
    claimDeviceByQrCode.mutate(
      { qr: qrCode },
      {
        onSuccess: ({ device }) => navigate(`/devices/${device.id}`),
        onError: () => navigate('/'),
      }
    );
  };

  // When a user scans QR code outside our app, and gets straight to the claiming page
  useEffectOnce(function autoClaimDevice() {
    const qrCode = searchParams.get(QR_SEARCH_PARAM) || params.cloudId;

    if (!qrCode) return;

    onClaim(qrCode);
  });

  const onSelect = (spaceId: SpaceType['id']) => {
    if (spaceId === selectedSpace?.id) {
      setSelectedSpace(null);

      return;
    }

    const space = find({ id: spaceId }, spaces.data);

    if (space && canView(space)) setSelectedSpace(space);
  };

  const onNext = () => {
    if (!selectedSpace?.id || noAccess(selectedSpace)) return;

    navigate(`/overview/${selectedSpace.id}`);
  };

  return (
    <PageContainer>
      <Box py="lg" px="md" className={classes.content}>
        <Title order={4} data-testid="spaces-title">
          Spaces
        </Title>

        <Box className={classes.tree}>
          <SpaceTree
            spaces={spaces.data}
            draggable={false}
            openModal={openModal}
            handleSelected={onSelect}
            selected={selectedSpace}
          />
        </Box>

        <Button
          disabled={!selectedSpace || selectedSpace?.space_type === 'root'}
          size="lg"
          onClick={onNext}
          data-testid="continue-button"
        >
          {!selectedSpace ? 'Select a space to continue' : 'Continue'}
        </Button>
      </Box>
    </PageContainer>
  );
}

const useStyles = createStyles((theme, params: { isAdmin: boolean }) => ({
  content: {
    display: 'grid',
    gridTemplateRows: 'min-content 1fr min-content',
    gridRowGap: theme.spacing.md,
  },

  tree: {
    border: `1px solid ${theme.colors.gray[3]}`,

    '.tree-container': {
      gridTemplateRows: params.isAdmin ? '0 110px 1fr' : '0 70px 1fr',

      '.tree-header': {
        '.tree-actions': {
          display: 'none',
        },

        input: {
          minHeight: 45,
        },
      },

      '.rc-tree-list': {
        '.rst_toggleButtonIcon': {
          width: 20,
          height: 20,
        },

        '.rc-tree-treenode': {
          height: '40px !important',

          '.inline-space-name-input': {
            fontSize: `${theme.fontSizes.md} !important`,
          },

          '.rc-tree-iconEle': {
            width: 20,
            height: 20,

            svg: {
              width: '20px !important',
              height: '20px !important',
            },
          },

          '.rc-tree-title': {
            paddingLeft: theme.spacing.md,
            fontSize: theme.fontSizes.md,
          },
        },
      },
    },
  },

  action: {
    backgroundColor: theme.colors.gray[2],
  },
}));
